import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Video } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/creatives`;
const MODELS = {
  kling: { label: "Kling — fără sunet", durations: ["5", "10"], perSec: 0.14 },
  seedance: { label: "Seedance 1.5 Pro — cu sunet", durations: ["5", "8", "10"], perSec: 0.06 },
};
const ARS = [["9:16", "Vertical 9:16"], ["1:1", "Pătrat 1:1"], ["16:9", "Orizontal 16:9"]];

const Pills = ({ items, value, onChange, id }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map(([k, l]) => (
      <button key={k} type="button" data-testid={`${id}-${k.replace(":", "x")}`} onClick={() => onChange(k)}
        className={`rounded-full border px-2.5 py-0.5 text-xs ${value === k ? "border-amber-400 bg-amber-400 text-zinc-950" : "border-zinc-700 text-zinc-400 hover:border-amber-400"}`}>{l}</button>
    ))}
  </div>
);

export const DirectVideoDialog = ({ photo, product, initialText, onClose, onStarted }) => {
  const [model, setModel] = useState("kling");
  const [duration, setDuration] = useState("5");
  const [ar, setAr] = useState("9:16");
  const [prompt, setPrompt] = useState(initialText || "");
  const [busy, setBusy] = useState(false);
  const cfg = MODELS[model];
  const pickModel = (m) => { setModel(m); if (!MODELS[m].durations.includes(duration)) setDuration("5"); };
  const arFromPhoto = photo && model === "kling";

  const go = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/video-direct`, { model, duration, aspect_ratio: ar, prompt, source_url: photo || null, sku: photo ? product?.sku : null });
      toast.success("Video-ul se generează (2–5 min) — apare în galerie");
      onStarted(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="direct-video-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Video direct</DialogTitle></DialogHeader>
        <div className="flex items-center gap-3 rounded-lg border border-zinc-800 p-2 text-xs text-zinc-400" data-testid="direct-video-source">
          {photo ? <><img src={photo} alt="" className="h-12 w-12 rounded object-contain bg-white" /> Pornește din poza selectată{product?.title ? ` · ${product.title}` : ""} — produsul rămâne identic.</>
            : <><Video className="h-5 w-5 text-amber-400" /> Nicio poză selectată — video-ul se creează doar din descriere.</>}
        </div>
        <div className="space-y-3">
          <div><p className="mb-1 text-xs text-zinc-500">Model</p><Pills id="direct-video-model" items={Object.entries(MODELS).map(([k, v]) => [k, v.label])} value={model} onChange={pickModel} /></div>
          <div><p className="mb-1 text-xs text-zinc-500">Durată</p><Pills id="direct-video-duration" items={cfg.durations.map((d) => [d, `${d} sec`])} value={duration} onChange={setDuration} /></div>
          <div><p className="mb-1 text-xs text-zinc-500">Format</p>
            {arFromPhoto ? <p className="text-xs text-zinc-500" data-testid="direct-video-ar-note">Kling păstrează formatul pozei.</p> : <Pills id="direct-video-ar" items={ARS} value={ar} onChange={setAr} />}
          </div>
          <label className="block text-xs text-zinc-500">Ce se întâmplă în video {photo ? "(opțional)" : ""}
            <textarea data-testid="direct-video-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} maxLength={1500}
              placeholder={photo ? "ex. camera se rotește lent în jurul produsului, abur care iese" : "ex. bucătărie luminoasă dimineața, cafea turnată în cană, muzică relaxată"}
              className="mt-1 w-full rounded-md border border-zinc-700 bg-zinc-950 p-2 text-sm text-zinc-100" />
          </label>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-zinc-500" data-testid="direct-video-cost">Cost estimat ~{(cfg.perSec * Number(duration)).toFixed(2)} $</span>
          <button data-testid="direct-video-submit" onClick={go} disabled={busy || (!photo && !prompt.trim())}
            className="flex items-center gap-1.5 rounded-full bg-amber-400 px-4 py-1.5 text-sm font-medium text-zinc-950 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />} Generează video
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
